import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    AreaChart,
    Area,
} from 'recharts';
import {
    FileText,
    Clock,
    CheckCircle,
    XCircle,
    Truck,
    TrendingUp,
    Users,
    Building,
    Calendar,
    RefreshCw,
    Download,
    Filter,
    BarChart3,
    PieChartIcon,
    Activity,
    Timer,
    Target,
    AlertTriangle,
} from 'lucide-react';

const COLORS = {
    pendiente: '#f59e0b',
    autorizado: '#3b82f6',
    despachado: '#10b981',
    rechazado: '#ef4444',
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function ReportesGuiasPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');

    useEffect(() => {
        // Establecer rango por defecto (últimos 30 días)
        const hoy = new Date();
        const hace30dias = new Date();
        hace30dias.setDate(hace30dias.getDate() - 30);
        
        setFechaFin(hoy.toISOString().split('T')[0]);
        setFechaInicio(hace30dias.toISOString().split('T')[0]);
    }, []);

    useEffect(() => {
        if (fechaInicio && fechaFin) {
            loadData();
        }
    }, [fechaInicio, fechaFin]);

    const loadData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (fechaInicio) params.append('fecha_inicio', fechaInicio);
            if (fechaFin) params.append('fecha_fin', fechaFin);

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/guias/reportes?${params.toString()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );

            if (!response.ok) throw new Error('Error al cargar datos');
            
            const result = await response.json();
            setData(result);
        } catch (err) {
            console.error('Error loading reportes:', err);
            toast.error('Error al cargar los reportes');
        } finally {
            setLoading(false);
        }
    };

    const setPresetRange = (days) => {
        const hoy = new Date();
        const inicio = new Date();
        inicio.setDate(inicio.getDate() - days);
        
        setFechaFin(hoy.toISOString().split('T')[0]);
        setFechaInicio(inicio.toISOString().split('T')[0]);
    };

    const formatMes = (mes) => {
        const [year, month] = mes.split('-');
        const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        return `${meses[parseInt(month) - 1]} ${year.slice(2)}`;
    };

    if (loading && !data) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-3">
                            <BarChart3 className="h-8 w-8 text-primary" />
                            Reportes y Métricas
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Análisis de gestión de guías de despacho
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={loadData}
                        className="border-2"
                        disabled={loading}
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Actualizar
                    </Button>
                </div>

                {/* Filtros */}
                <Card className="border-2">
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="flex-1 grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                                        Fecha Inicio
                                    </Label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            type="date"
                                            value={fechaInicio}
                                            onChange={(e) => setFechaInicio(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                                        Fecha Fin
                                    </Label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            type="date"
                                            value={fechaFin}
                                            onChange={(e) => setFechaFin(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => setPresetRange(7)}>
                                    7 días
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setPresetRange(30)}>
                                    30 días
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setPresetRange(90)}>
                                    90 días
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setPresetRange(365)}>
                                    1 año
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {data && (
                    <>
                        {/* KPIs Principales */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            <Card className="border-2">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Guías</p>
                                            <p className="text-3xl font-black mt-1">{data.resumen.total}</p>
                                        </div>
                                        <div className="p-3 bg-primary/10 rounded-lg">
                                            <FileText className="h-6 w-6 text-primary" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-2 border-amber-500/30">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Pendientes</p>
                                            <p className="text-3xl font-black mt-1 text-amber-600">{data.resumen.pendientes}</p>
                                        </div>
                                        <div className="p-3 bg-amber-500/10 rounded-lg">
                                            <Clock className="h-6 w-6 text-amber-600" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-2 border-blue-500/30">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Autorizadas</p>
                                            <p className="text-3xl font-black mt-1 text-blue-600">{data.resumen.autorizados}</p>
                                        </div>
                                        <div className="p-3 bg-blue-500/10 rounded-lg">
                                            <CheckCircle className="h-6 w-6 text-blue-600" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-2 border-emerald-500/30">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Despachadas</p>
                                            <p className="text-3xl font-black mt-1 text-emerald-600">{data.resumen.despachados}</p>
                                        </div>
                                        <div className="p-3 bg-emerald-500/10 rounded-lg">
                                            <Truck className="h-6 w-6 text-emerald-600" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-2 border-red-500/30">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Rechazadas</p>
                                            <p className="text-3xl font-black mt-1 text-red-600">{data.resumen.rechazados}</p>
                                        </div>
                                        <div className="p-3 bg-red-500/10 rounded-lg">
                                            <XCircle className="h-6 w-6 text-red-600" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Indicadores de Eficiencia */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Card className="border-2">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="p-4 bg-emerald-500/10 rounded-xl">
                                            <Target className="h-8 w-8 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Tasa de Aprobación</p>
                                            <p className="text-4xl font-black text-emerald-600">{data.tasas.aprobacion}%</p>
                                            <p className="text-xs text-muted-foreground mt-1">Guías autorizadas o despachadas</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-2">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="p-4 bg-blue-500/10 rounded-xl">
                                            <Timer className="h-8 w-8 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Tiempo Prom. Aprobación</p>
                                            <p className="text-4xl font-black text-blue-600">{data.tiempos_promedio.aprobacion_horas}h</p>
                                            <p className="text-xs text-muted-foreground mt-1">Desde creación hasta autorización</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-2">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="p-4 bg-amber-500/10 rounded-xl">
                                            <Activity className="h-8 w-8 text-amber-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">Tiempo Prom. Despacho</p>
                                            <p className="text-4xl font-black text-amber-600">{data.tiempos_promedio.despacho_horas}h</p>
                                            <p className="text-xs text-muted-foreground mt-1">Desde autorización hasta despacho</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Gráficos */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Tendencia Mensual */}
                            <Card className="border-2 lg:col-span-2">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <TrendingUp className="h-5 w-5" />
                                        Tendencia Mensual
                                    </CardTitle>
                                    <CardDescription>Evolución de guías por estado en los últimos meses</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={data.tendencia_mensual}>
                                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                                <XAxis 
                                                    dataKey="mes" 
                                                    tickFormatter={formatMes}
                                                    className="text-xs"
                                                />
                                                <YAxis className="text-xs" />
                                                <Tooltip 
                                                    labelFormatter={formatMes}
                                                    contentStyle={{ 
                                                        backgroundColor: 'hsl(var(--card))',
                                                        border: '1px solid hsl(var(--border))',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Legend />
                                                <Area 
                                                    type="monotone" 
                                                    dataKey="despachado" 
                                                    stackId="1"
                                                    stroke={COLORS.despachado}
                                                    fill={COLORS.despachado}
                                                    fillOpacity={0.6}
                                                    name="Despachadas"
                                                />
                                                <Area 
                                                    type="monotone" 
                                                    dataKey="autorizado" 
                                                    stackId="1"
                                                    stroke={COLORS.autorizado}
                                                    fill={COLORS.autorizado}
                                                    fillOpacity={0.6}
                                                    name="Autorizadas"
                                                />
                                                <Area 
                                                    type="monotone" 
                                                    dataKey="pendiente" 
                                                    stackId="1"
                                                    stroke={COLORS.pendiente}
                                                    fill={COLORS.pendiente}
                                                    fillOpacity={0.6}
                                                    name="Pendientes"
                                                />
                                                <Area 
                                                    type="monotone" 
                                                    dataKey="rechazado" 
                                                    stackId="1"
                                                    stroke={COLORS.rechazado}
                                                    fill={COLORS.rechazado}
                                                    fillOpacity={0.6}
                                                    name="Rechazadas"
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Distribución por Estado */}
                            <Card className="border-2">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <PieChartIcon className="h-5 w-5" />
                                        Distribución por Estado
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={data.por_estado}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={90}
                                                    dataKey="cantidad"
                                                    nameKey="estado"
                                                    label={({ estado, percent }) => 
                                                        `${estado} (${(percent * 100).toFixed(0)}%)`
                                                    }
                                                    labelLine={false}
                                                >
                                                    {data.por_estado.map((entry, index) => (
                                                        <Cell 
                                                            key={`cell-${index}`} 
                                                            fill={COLORS[entry.estado] || PIE_COLORS[index % PIE_COLORS.length]} 
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip 
                                                    contentStyle={{ 
                                                        backgroundColor: 'hsl(var(--card))',
                                                        border: '1px solid hsl(var(--border))',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Distribución por Tipo */}
                            <Card className="border-2">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="h-5 w-5" />
                                        Distribución por Tipo
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={data.por_tipo} layout="vertical">
                                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                                <XAxis type="number" className="text-xs" />
                                                <YAxis 
                                                    type="category" 
                                                    dataKey="tipo" 
                                                    className="text-xs"
                                                    width={100}
                                                    tickFormatter={(val) => val.charAt(0).toUpperCase() + val.slice(1)}
                                                />
                                                <Tooltip 
                                                    contentStyle={{ 
                                                        backgroundColor: 'hsl(var(--card))',
                                                        border: '1px solid hsl(var(--border))',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Bar 
                                                    dataKey="cantidad" 
                                                    fill="hsl(var(--primary))"
                                                    radius={[0, 4, 4, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Rankings */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Top Solicitantes */}
                            <Card className="border-2">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Users className="h-5 w-5" />
                                        Top 10 Solicitantes
                                    </CardTitle>
                                    <CardDescription>Usuarios con más guías creadas</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {data.top_solicitantes.map((item, idx) => (
                                            <div key={idx} className="flex items-center gap-3">
                                                <div className={`
                                                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                                                    ${idx === 0 ? 'bg-amber-500 text-white' : 
                                                      idx === 1 ? 'bg-gray-400 text-white' : 
                                                      idx === 2 ? 'bg-amber-700 text-white' : 
                                                      'bg-muted text-muted-foreground'}
                                                `}>
                                                    {idx + 1}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-medium text-sm">{item.nombre}</p>
                                                    <div className="w-full bg-muted rounded-full h-2 mt-1">
                                                        <div 
                                                            className="bg-primary h-2 rounded-full transition-all"
                                                            style={{ 
                                                                width: `${(item.cantidad / data.top_solicitantes[0]?.cantidad) * 100}%` 
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <span className="font-mono font-bold text-sm">{item.cantidad}</span>
                                            </div>
                                        ))}
                                        {data.top_solicitantes.length === 0 && (
                                            <p className="text-center text-muted-foreground py-4">Sin datos</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Top Destinos */}
                            <Card className="border-2">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Building className="h-5 w-5" />
                                        Top 10 Empresas Destino
                                    </CardTitle>
                                    <CardDescription>Empresas con más guías recibidas</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {data.top_destinos.map((item, idx) => (
                                            <div key={idx} className="flex items-center gap-3">
                                                <div className={`
                                                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                                                    ${idx === 0 ? 'bg-emerald-500 text-white' : 
                                                      idx === 1 ? 'bg-emerald-400 text-white' : 
                                                      idx === 2 ? 'bg-emerald-300 text-white' : 
                                                      'bg-muted text-muted-foreground'}
                                                `}>
                                                    {idx + 1}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-medium text-sm truncate" title={item.nombre}>
                                                        {item.nombre}
                                                    </p>
                                                    <div className="w-full bg-muted rounded-full h-2 mt-1">
                                                        <div 
                                                            className="bg-emerald-500 h-2 rounded-full transition-all"
                                                            style={{ 
                                                                width: `${(item.cantidad / data.top_destinos[0]?.cantidad) * 100}%` 
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <span className="font-mono font-bold text-sm">{item.cantidad}</span>
                                            </div>
                                        ))}
                                        {data.top_destinos.length === 0 && (
                                            <p className="text-center text-muted-foreground py-4">Sin datos</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Alerta si hay muchos pendientes */}
                        {data.resumen.pendientes > 5 && (
                            <Card className="border-2 border-amber-500 bg-amber-500/5">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-4">
                                        <AlertTriangle className="h-8 w-8 text-amber-600" />
                                        <div>
                                            <p className="font-bold text-amber-600">
                                                Hay {data.resumen.pendientes} guías pendientes de revisión
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                Se recomienda revisar y procesar las guías pendientes para mantener un flujo óptimo
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}
